import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-sans/700.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@fontsource/ibm-plex-mono/600.css';
import '@fontsource/bricolage-grotesque/800.css';
import '@platform/ui/tokens.css';
import './styles.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './app/App.js';
import { AppProvider, type Session } from './app/context.js';
import { httpApi, type Api } from './api/client.js';
import { loadConfig } from './config.js';
import { amplifyAuth, amplifyMfa, configureAmplify, isPublicPath, type AuthClient } from './lib/auth.js';
import type { Mfa } from './lib/mfa.js';

const root = createRoot(document.getElementById('root')!);

async function boot() {
  const cfg = await loadConfig();
  let api: Api;
  let mfa: Mfa;
  let auth: AuthClient;
  let session: Session;

  // Direkt die Build-Konstante prüfen, damit Vite den Mock-Zweig im Produktions-Build entfernt.
  if (import.meta.env.VITE_MOCK === 'true') {
    const params = new URLSearchParams(location.search);
    const stored = sessionStorage.getItem('mock.role');
    const role = (params.get('role') ?? stored ?? 'tenantAdmin') as 'member' | 'tenantAdmin' | 'platformAdmin';
    sessionStorage.setItem('mock.role', role);
    const mock = (await import('./api/mock.js')).mockApi(role);
    api = mock.api;
    mfa = mock.mfa;
    auth = (await import('./lib/mock-auth.js')).mockAuth();
    const next = { member: 'tenantAdmin', tenantAdmin: 'platformAdmin', platformAdmin: 'member' } as const;
    session = {
      mock: true,
      pilot: params.has('pilot'),
      refresh: async () => {},
      logout: () => {
        sessionStorage.setItem('mock.role', next[role]);
        location.assign('/');
      },
    };
  } else {
    configureAmplify(cfg);
    const client = amplifyAuth();
    auth = client;
    api = httpApi(() => client.idToken());
    mfa = amplifyMfa();
    session = { mock: false, pilot: cfg.pilot === true, refresh: () => client.refresh(), logout: () => void client.signOut().finally(() => location.assign('/anmelden')) };
  }

  // Alles außer Anmeldung, Impressum und Datenschutz setzt eine Anmeldung voraus.
  if (!isPublicPath(location.pathname) && !(await auth.hasSession())) {
    location.replace(`/anmelden?weiter=${encodeURIComponent(location.pathname + location.search)}`);
    return;
  }

  const queryClient = new QueryClient({
    defaultOptions: { queries: { staleTime: 30_000, retry: (n, err) => n < 2 && ![401, 403, 404].includes((err as { status?: number }).status ?? 0) } },
  });

  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AppProvider api={api} mfa={mfa} auth={auth} session={session}>
          <App />
        </AppProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

boot().catch((err: Error) =>
  root.render(
    <div className="boot-message">
      <h1>Betriebsam konnte nicht starten</h1>
      <p>{err.message}</p>
    </div>,
  ),
);
